// src/views/examples/ReportCustomer.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardFooter,
  CardBody,
  Table,
  Container,
  Row,
  Col,
  Button,
  Input,
} from "reactstrap";
import Header from "components/Headers/Header";
import { useTablePaging, PAGE_SIZE_OPTIONS } from "../stores/pagingStore";
import LoadingSpinner from "components/LoadingSpinner";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";

const TABLE_KEY = "customer_reports";

// ✅ BASE จาก env
const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
const apiCustomerReportsUrl = (customerId) =>
  `${API_BASE}/customers/${encodeURIComponent(String(customerId))}/reports`;

const ReportCustomer = () => {
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ รับ customer จาก state
  const customer = location.state?.customer;

  const customerId =
    customer?.customerId ||
    customer?.id ||
    customer?.cmId ||
    customer?.customer_id ||
    customer?.cmCode;

  const { page, pageSize, setPage, setPageSize } = useTablePaging(TABLE_KEY);

  const [isSmall, setIsSmall] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [rows, setRows] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState("");

  // ✅ loading ต่อแถว ตอน toggle
  const [togglingId, setTogglingId] = useState(null);

  const customerName = customer?.customerName ?? customer?.cmName ?? "-";

  const loadCustomerReports = async () => {
    try {
      if (!API_BASE) throw new Error("ไม่ได้ตั้งค่า REACT_APP_API_BASE");
      if (!customerId) throw new Error("ไม่พบ CustomerId");

      setIsLoading(true);
      setErr("");

      const url = apiCustomerReportsUrl(customerId);
      const res = await axios.get(url, { timeout: 15000 });

      // ✅ response: { customerId, reports: [...] }
      const list = Array.isArray(res?.data?.reports) ? res.data.reports : [];
      setRows(list);

      // ✅ รีเซ็ตไปหน้าแรกหลังโหลด
      setPage(TABLE_KEY, 1);
    } catch (e) {
      setRows([]);
      setErr(e?.response?.data?.message || e?.message || "โหลดข้อมูลไม่สำเร็จ");
      setPage(TABLE_KEY, 1);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCustomerReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const getId = (it, i) =>
    it.id ??
    it.customerReportId ??
    it.reportId ??
    it.ID ??
    `${it.reportKey ?? it.code ?? "row"}-${i}`;

  const norm = (s) => (s || "").toString().toLowerCase().trim();

  // ✅ normalize row ให้ table ใช้ได้เหมือนเดิม
  const mappedRows = useMemo(() => {
    return (rows || []).map((x, i) => ({
      id: getId(x, i),
      code: x.reportKey ?? x.code ?? x.reportCode ?? "",
      name: x.menuName ?? x.reportName ?? x.name ?? "",
      status:
        typeof x.status === "boolean"
          ? x.status
          : typeof x.use === "boolean"
            ? x.use
            : true,
      active:
        typeof x.isActive === "boolean"
          ? x.isActive
          : typeof x.active === "boolean"
            ? x.active
            : String(x.isActive ?? "Y").toUpperCase() === "Y",
      _raw: x,
    }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const k = norm(keyword);
    if (!k) return mappedRows; // ✅ โชว์ทั้งหมดทั้ง Active/Inactive
    return mappedRows.filter((r) => {
      const blob = [r.code, r.name, r.active ? "active" : "inactive"]
        .filter(Boolean)
        .join(" ");
      return norm(blob).includes(k);
    });
  }, [mappedRows, keyword]);

  const total = filteredRows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount);
  const startIdx = (current - 1) * pageSize;
  const displayRows = filteredRows.slice(startIdx, startIdx + pageSize);

  const buildPages = (p, max) => {
    const arr = [];
    const start = Math.max(1, p - 1);
    const end = Math.min(max, p + 1);
    if (start > 1) arr.push(1);
    for (let i = start; i <= end; i++) arr.push(i);
    if (end < max) arr.push(max);
    return [...new Set(arr)];
  };
  const pages = buildPages(current, pageCount);

  useEffect(() => {
    setPage(TABLE_KEY, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, pageSize]);

  // ✅ Toggle isActive -> POST /reports/{customerReportId}/status
  const toggleStatus = async (row) => {
    const id = row?._raw?.customerReportId ?? row?._raw?.id ?? row?.id;
    if (!id) return;

    if (!API_BASE) {
      await MySwal.fire({
        icon: "error",
        title: "Config error",
        text: "ไม่ได้ตั้งค่า REACT_APP_API_BASE",
      });
      return;
    }
    if (!customerId) {
      await MySwal.fire({
        icon: "error",
        title: "Missing customerId",
        text: "ไม่พบ CustomerId",
      });
      return;
    }

    const nextActive = !row.active;

    try {
      setTogglingId(id);

      const url = `${API_BASE}/reports/${encodeURIComponent(String(id))}/status`;

      await axios.put(
        url,
        {
          customerId: String(customerId),
          isActive: Boolean(nextActive),
        },
        { timeout: 15000 },
      );

      // ✅ update local rows (optimistic after success)
      setRows((prev) =>
        (prev || []).map((x, i) => {
          const rid = getId(x, i);
          if (String(rid) !== String(id)) return x;

          // update both possible keys
          return {
            ...x,
            isActive: Boolean(nextActive),
            active: Boolean(nextActive),
          };
        }),
      );
    } catch (e) {
      await MySwal.fire({
        icon: "error",
        title: "Update failed",
        text:
          e?.response?.data?.message || e?.message || "ไม่สามารถอัปเดตสถานะได้",
      });
    } finally {
      setTogglingId(null);
    }
  };

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <>
      <style>{`
        .zi-pill{
          display:inline-flex; align-items:center; width:fit-content;
          padding:6px 10px; border-radius:999px; font-size:12px; font-weight:800;
          border:1px solid rgba(15,23,42,.10); background:#fff; color:#0f172a;
        }
        .zi-pill.on{ background: rgba(45,206,137,.12); border-color: rgba(45,206,137,.22); color:#0f5132; }
        .zi-pill.off{ background: rgba(245,54,92,.10); border-color: rgba(245,54,92,.18); color:#7a1026; }

        .zi-table-wrap{ background:#fff; border-radius:10px; border:1px solid rgba(0,0,0,.06); overflow:hidden; }
        .zi-table{ margin:0; }
        .zi-table thead th{ background:#f7fafc; color:#0f172a; font-weight:700; font-size:14px; border-bottom:1px solid rgba(0,0,0,.06); padding:14px 16px; white-space:nowrap; }
        .zi-table tbody td{ padding:14px 16px; border-top:1px solid rgba(0,0,0,.06); vertical-align:middle; font-size:14px; color:#0f172a; }
        .zi-table tbody tr:hover{ background:#f9fbff; }
        .fw-600{ font-weight:600; }
        .gap-2{ gap:8px; }
        .paging-wrap{ display:flex; justify-content:center; gap:8px; }
        .paging-btn{ border:1px solid rgba(0,0,0,.1); background:#fff; padding:6px 10px; border-radius:8px; min-width:36px; }
        .paging-btn.active{ font-weight:800; }
        .paging-btn:disabled{ opacity:.5; cursor:not-allowed; }

        /* ✅ Toggle switch */
        .zi-switch {
          position: relative;
          display: inline-block;
          width: 46px;
          height: 24px;
        }
        .zi-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .zi-slider {
          position: absolute;
          cursor: pointer;
          inset: 0;
          background-color: #e5e7eb;
          transition: 0.2s;
          border-radius: 999px;
        }
        .zi-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          top: 3px;
          background-color: white;
          transition: 0.2s;
          border-radius: 50%;
        }
        .zi-switch input:checked + .zi-slider {
          background-color: #2dce89;
        }
        .zi-switch input:checked + .zi-slider:before {
          transform: translateX(22px);
        }
        .zi-switch.disabled {
          opacity: 0.6;
          pointer-events: none;
        }
      `}</style>

      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="border-0 d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="mb-0">Customer Reports</h6>
                  <div className="text-muted small mt-1">
                    Customer: {customerName}
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    color="info"
                    outline
                    onClick={loadCustomerReports}
                    disabled={!customerId}
                  >
                    Refresh
                  </Button>

                  <Button
                    color="success"
                    onClick={() =>
                      navigate("/add-report-customer", { state: { customer } })
                    }
                  >
                    Add Report
                  </Button>
                </div>
              </CardHeader>

              <CardBody>
                {err ? <div className="text-danger mb-3">{err}</div> : null}

                <div className="mb-3">
                  <Row className="align-items-center g-0">
                    <Col
                      xs="12"
                      md="6"
                      className="d-flex align-items-center flex-wrap"
                      style={{ gap: 8 }}
                    >
                      <Input
                        type="select"
                        value={pageSize}
                        onChange={(e) =>
                          setPageSize(TABLE_KEY, Number(e.target.value))
                        }
                        style={{ width: 96 }}
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Input>
                      <span className="text-muted">Row/Page</span>
                    </Col>

                    <Col
                      xs="12"
                      md="6"
                      className="mt-2 mt-md-0 d-flex justify-content-md-end"
                    >
                      <Input
                        type="text"
                        placeholder="Report/Status"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        className="w-100 w-md-auto"
                        style={{ maxWidth: 360, minWidth: 280 }}
                      />
                    </Col>
                  </Row>
                </div>

                {/* ✅ TABLE */}
                {isSmall ? (
                  <Table responsive hover className="table mb-0">
                    <thead>
                      <tr>
                        <th className="text-start">#</th>
                        <th className="text-start">Index</th>
                        <th className="text-start">Report Name</th>
                        <th className="text-start">Status</th>
                        <th className="text-start" style={{ width: 180 }}>
                          Management
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center text-muted">
                            ยังไม่มีข้อมูล
                          </td>
                        </tr>
                      ) : (
                        displayRows.map((it, i) => {
                          const rowNo = startIdx + i + 1;
                          const isBusy = togglingId === it.id;
                          return (
                            <tr key={it.id ?? `${i}`}>
                              <td>{rowNo}</td>
                              <td>{it.code || "-"}</td>
                              <td className="fw-600">{it.name || "-"}</td>
                              <td>{it.active ? "Active" : "Inactive"}</td>
                              <td>
                                <div className="d-inline-flex gap-2">
                                  <label
                                    className={`zi-switch ${
                                      isBusy ? "disabled" : ""
                                    }`}
                                    title="Toggle Active"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={!!it.active}
                                      onChange={() => toggleStatus(it)}
                                      disabled={isBusy}
                                    />
                                    <span className="zi-slider"></span>
                                  </label>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                ) : (
                  <div className="zi-table-wrap">
                    <Table responsive hover className="zi-table mb-0">
                      <thead>
                        <tr>
                          <th className="text-start" style={{ width: 90 }}>
                            #
                          </th>
                          <th className="text-start" style={{ width: 180 }}>
                            Index
                          </th>
                          <th className="text-start" style={{ width: 180 }}>
                            Report Name
                          </th>
                          <th className="text-start" style={{ width: 160 }}>
                            Status
                          </th>
                          <th className="text-end" style={{ width: 180 }}>
                            Management
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {displayRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="text-center text-muted py-4"
                            >
                              ยังไม่มีข้อมูล
                            </td>
                          </tr>
                        ) : (
                          displayRows.map((it, i) => {
                            const rowNo = startIdx + i + 1;
                            const isBusy = togglingId === it.id;
                            return (
                              <tr key={it.id ?? `${i}`}>
                                <td className="text-start fw-600">{rowNo}</td>
                                <td className="text-start">{it.code || "-"}</td>
                                <td className="text-start fw-600">
                                  {it.name || "-"}
                                </td>
                                <td className="text-start">
                                  <span
                                    className={`zi-pill ${
                                      it.active ? "on" : "off"
                                    }`}
                                  >
                                    {it.active ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td className="text-end">
                                  <div className="d-inline-flex gap-2 justify-content-end">
                                    <label
                                      className={`zi-switch ${
                                        isBusy ? "disabled" : ""
                                      }`}
                                      title="Toggle Active"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={!!it.active}
                                        onChange={() => toggleStatus(it)}
                                        disabled={isBusy}
                                      />
                                      <span className="zi-slider"></span>
                                    </label>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                <div className="d-flex justify-content-end mt-2">
                  <div className="text-muted small">
                    Showing {total === 0 ? 0 : startIdx + 1}–
                    {Math.min(startIdx + displayRows.length, total)} of {total}{" "}
                    entries
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-4">
                  <Button color="secondary" onClick={() => navigate(-1)}>
                    Back
                  </Button>
                </div>
              </CardBody>

              {total > 0 && (
                <CardFooter
                  className="py-4"
                  style={{ position: "relative", zIndex: 10 }}
                >
                  <div className="paging-wrap">
                    <button
                      className="paging-btn icon"
                      onClick={() =>
                        setPage(TABLE_KEY, Math.max(1, current - 1))
                      }
                      disabled={current === 1}
                      aria-label="Previous page"
                    >
                      ‹
                    </button>

                    {pages.map((p) => (
                      <button
                        key={p}
                        className={`paging-btn ${
                          current === p ? "active" : ""
                        }`}
                        onClick={() => setPage(TABLE_KEY, p)}
                      >
                        {p}
                      </button>
                    ))}

                    <button
                      className="paging-btn icon"
                      onClick={() =>
                        setPage(TABLE_KEY, Math.min(pageCount, current + 1))
                      }
                      disabled={current === pageCount}
                      aria-label="Next page"
                    >
                      ›
                    </button>
                  </div>
                </CardFooter>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ReportCustomer;
